import {
    type ReactElement,
    isValidElement
} from "react";
import type {
    ToggleGroupNavigationIndexConstantType,
    ToggleGroupNextValueConstantType,
    ToggleGroupTabStopConstantType,
    ToggleGroupKeyStepConstantType,
    ToggleGroupOrderedConstantType,
    ToggleGroupValuesConstantType,
    ToggleGroupItemsConstantType,
    ToggleGroupItemInfo
} from "./type";
import {
    flattenAttachedGroupChildren
} from "../attachedGroup/stylesheet";
import {
    getHorizontalStep
} from "../../helpers/direction";

export const TOGGLE_GROUP_PART = "item";

export const TOGGLE_GROUP_NAVIGATION_KEYS: Array<string> = [
    "ArrowRight",
    "ArrowLeft",
    "ArrowDown",
    "ArrowUp",
    "Home",
    "End"
];

export const isToggleGroupItem = (node: ReactElement): boolean => {
    const elementType: unknown = node.type;

    if(typeof elementType !== "function" || !("toggleGroupPart" in elementType)) {
        return false;
    }

    return elementType.toggleGroupPart === TOGGLE_GROUP_PART;
};

export const getToggleGroupItems = ({
    children
}: ToggleGroupItemsConstantType): Array<ToggleGroupItemInfo> => {
    const items: Array<ToggleGroupItemInfo> = [];

    flattenAttachedGroupChildren({
        children
    }).forEach((element) => {
        if(!isValidElement<{
            isDisabled?: boolean;
            value?: unknown;
        }>(element) || !isToggleGroupItem(element) || typeof element.props.value !== "string") {
            return;
        }

        items.push({
            isDisabled: element.props.isDisabled === true,
            value: element.props.value
        });
    });

    return items;
};

export const getToggleGroupValues = ({
    value
}: ToggleGroupValuesConstantType): Array<string> => {
    if(Array.isArray(value)) {
        return value;
    }

    return value ? [
        value
    ] : [];
};

export const getToggleGroupNextValue = ({
    selectBehaviour,
    isClearEnabled,
    values,
    value
}: ToggleGroupNextValueConstantType): string | Array<string> | undefined => {
    const isOn = values.includes(value);

    if(selectBehaviour === "multiple") {
        return isOn ? values.filter((current) => current !== value) : [
            ...values,
            value
        ];
    }

    if(!isOn) {
        return value;
    }

    return isClearEnabled ? "" : undefined;
};

export const getToggleGroupTabStopValue = ({
    lastFocused,
    isDisabled,
    values,
    items
}: ToggleGroupTabStopConstantType): string | undefined => {
    if(isDisabled) {
        return undefined;
    }

    const enabledItems = items.filter((item) => !item.isDisabled);

    if(lastFocused && enabledItems.some((item) => item.value === lastFocused)) {
        return lastFocused;
    }

    return enabledItems.find((item) => values.includes(item.value))?.value ?? enabledItems[0]?.value;
};

export const getToggleGroupOrderedValues = ({
    entries,
    items
}: ToggleGroupOrderedConstantType): Array<string> => {
    return items.filter((item) => {
        const entry = entries.get(item.value);

        return !item.isDisabled && !!entry && !entry.isDisabled;
    }).map((item) => item.value);
};

export const getToggleGroupKeyStep = ({
    orientation,
    direction,
    key
}: ToggleGroupKeyStepConstantType): number => {
    if(orientation === "vertical") {
        if(key === "ArrowDown") {
            return 1;
        }

        return key === "ArrowUp" ? -1 : 0;
    }

    return getHorizontalStep({
        direction,
        key
    });
};

export const getToggleGroupNavigationIndex = ({
    current,
    count,
    step,
    key
}: ToggleGroupNavigationIndexConstantType): number => {
    if(!count) {
        return -1;
    }

    if(key === "Home") {
        return 0;
    }

    if(key === "End") {
        return count - 1;
    }

    if(!step) {
        return -1;
    }

    if(current < 0) {
        return step > 0 ? 0 : count - 1;
    }

    return (current + step + count) % count;
};
